<template>
	<Application v-model:error-title="errorTitle" class="TradeHubSettingsView common-form"
		:loading="spLoading || productGroupLoading || provisionLoading" :error-detail="errorDetail"
		:style="{ 'padding-left': isSideBarFixed ? '315px' : '15px' }">
		<template #navbar>
			<span style="font-size: 17px; font-weight: 600; line-height: 22px; padding-left: 24px;">{{
				$t('text.tradeHubSettings') }}</span>
		</template>
		<SideBar class="common-sidebar" :navigation="sections" :preselectedActiveLink="activeLink"
			:class="{ mobileSidebar: isMobile }" />

		<div class="content-container">
			<Disclosure :title="$t('text.provisionSettings')" expanded ref="SECTION_provisionSettings"
				class="common-disclosure" :lock="userIsImpersonating && false">
				<FieldSet :label="$t('text.resellerProvision')" :info-text="$t('text.resellerProvisionInfoText')">
					<Field 
						v-model="model.provision"
						:title="$t('text.generalProvision')"
						:info-text="$t('text.generalProvisionInfoText')"
						typeName="GeneralProvision"
						fieldName="provision"
						dataCy="provision"
						placeholder="0.00"
						@blur="updateGeneralProvision"
					/>
				</FieldSet>
				<FieldSet :label="$t('text.specificProvisions')" :info-text="$t('text.specificProvisionsInfoText')">
					<div class="specific-provisions">
						<div class="specific-provisions__header">
							<div class="specific-provisions__filter">
								<v-combobox
									v-model="selectedTourOperatorFilter"
									:items="tourOperatorFilterOptions"
									item-title="title"
									item-value="id"
									variant="outlined"
									density="compact"
									multiple
									chips
									closable-chips
									clearable
									hide-details
									@update:modelValue="onTourOperatorFilterChange"
									:placeholder="$t('text.filterByTourOperator')"
								>
									<template #prepend-inner>
										<v-icon size="small">mdi-magnify</v-icon>
									</template>
								</v-combobox>
							</div>
							<AddButton @click="openDialog({})" buttonText="provision" dataCy="add-provision" class="add-button" />
						</div>
						<div class="specific-provisions__empty" v-show="!filteredSpecificProvisions?.length">
							<p>{{ selectedTourOperatorFilter?.length > 0 ? $t('text.noProvisionsForSelectedTourOperator') : $t('text.noProvisionsAdded') }}</p>
						</div>
						<table v-if="filteredSpecificProvisions?.length" class="specific-provisions__table">
							<thead>
								<th>{{ $t('text.reseller') }}</th>
								<th>{{ $t('text.product') }}</th>
								<th>{{ $t('text.productGroup') }}</th>
								<th>{{ $t('text.provision') }}</th>
								<th></th>
							</thead>
							<tbody>
								<template v-for="(sProvision, index) of filteredSpecificProvisions" :key="sProvision.id">
									<tr @click='toggle(getOriginalIndex(sProvision))' :class="{ open: expanded === getOriginalIndex(sProvision)}">
										<td><div><v-icon color="#999999">mdi-menu-right</v-icon> <p>{{ getResellerNames(sProvision) }}</p></div></td>
										<td><p>{{ sProvision.products?.length > 0 ? sProvision.products?.length : '-' }}</p></td>
										<td><p>{{ sProvision.productGroups?.length > 0 ? sProvision.productGroups?.length : '-' }}</p></td>
										<td><p>{{ `${sProvision.provision} %` }}</p></td>
										<td><div><v-icon @click.stop.prevent="openDialog(sProvision)" data-cy="edit-provision">mdi-pencil</v-icon><v-icon @click.stop.prevent="openDeleteDialog(getOriginalIndex(sProvision))" data-cy="delete-provision">mdi-delete</v-icon></div></td>
									</tr>
									<tr class="expanded-row" v-if='expanded === getOriginalIndex(sProvision)'>
										<td colspan='5'>
											<SpecificProvisionDetail :specific-provision="expandedSpecificProvision" :tour-operators="tourOperators" :product-groups="productGroups" :service-provider-id="serviceProviderExternalId" :client-id="clientId" />
										</td>
									</tr>
								</template>
							</tbody>
						</table>
					</div>
				</FieldSet>
			</Disclosure>
		</div>
		<SpecificProvisionDialog ref="specificProvisionDialog" v-model="specificProvision" :tour-operators="tourOperators" :product-groups="productGroups" :service-provider-id="serviceProviderExternalId" :client-id="clientId" @update:modelValue="updateSpecificProvision" />
		<Dialog ref="deleteDialog"
			:confirmLabel="$t('text.delete')"
			:cancelLabel="$t('text.cancel')"
			:confirm-handler="onDeleteConfirmed"
			:isDelete="true"
			:showClose="true"
			:title="$t('text.deleteProvision')" 
			height="350px"
			width="550px"
		>
			<template #content>
				<v-row align="center" justify="center">
					<v-col style="text-align: center;">
						<img class="warningDialogImage" src="@/assets/icons/icon-warning.svg" />
					</v-col>
					<v-col cols="8" class="pr-5">
						<h2>{{ $t('text.deleteProvision') }}</h2>
						<p>{{ $t('text.deleteProvisionInfoText') }}</p>
					</v-col>
				</v-row>
			</template>
		</Dialog>
	</Application>
</template>

<script>
import Common from '@/mixins/Common.vue'
import SideBar from "@/components/common/SideBar.vue"
import Application from '../Application.vue'
import Disclosure from '../../../components/common/Disclosure.vue'
import FieldSet from '../packageDesigner/FieldSet.vue'
import Field from '@/components/fields/Field.vue'
import SpecificProvisionDialog from '../../../components/tradeHub/SpecificProvisionDialog.vue'
import SpecificProvisionDetail from '../../../components/tradeHub/SpecificProvisionDetail.vue'
import Dialog from '../../../components/common/Dialog.vue'
import TourOperatorExecutive from '../../../../../api2/src/modules/trade-hub/TourOperatorExecutive'
import AddButton from '../packageDesigner/AddButton.vue'
import Toast from '@/mixins/Toast.vue'
import Loading from '@/mixins/Loading.vue'

export default {
	name: 'TradeHubSettingsView',
	components: { SideBar, Application, Disclosure, FieldSet, Field, Dialog, SpecificProvisionDialog, SpecificProvisionDetail, AddButton, },
	mixins: [Common, Toast, Loading],
	data() {
		return {
			errorTitle: null,
			errorDetail: null,
			sections: [{ id: 'provisionSettings', state: 'valid' }],
			activeLink: 'provisionSettings',
			model: {
				specificProvisions: []
			},
			specificProvision: {},
			expandedSpecificProvision: {},
			spLoading: false,
			productGroupLoading: false,
			provisionLoading: false,
			tourOperators: [],
			productGroups: [],
			expanded: null,
			deleteIndex: null,
			selectedTourOperatorFilter: [],
		}
	},
	computed: {
		locale() {
			return this.$store.state.serviceLocale ?? 'de'
		},
		serviceProviderExternalId() {
			return this.$store.state.selectedServiceProvider?.fields.clientAssignments.de.find(ca => ca?.fields?.client?.de?.fields?.shopClientId?.de === this.$store.state.selectedClient.fields.shopClientId.de)?.fields?.externalId?.de
		},
		serviceProviderId() {
			return this.$store.state.selectedServiceProvider.sys.id
		},
		clientId() {
			return this.$store.state.selectedClient.sys.id
		},
		tourOperatorFilterOptions() {
			// Get all unique tour operator IDs that appear in at least one provision rule
			const tourOperatorIdsInProvisions = new Set()
			if (this.model.specificProvisions) {
				this.model.specificProvisions.forEach(provision => {
					if (provision.resellers) {
						provision.resellers.forEach(resellerId => {
							tourOperatorIdsInProvisions.add(resellerId)
						})
					}
				})
			}

			// Filter tour operators to only include those that are part of at least one provision rule
			return this.tourOperators
				.filter(to => tourOperatorIdsInProvisions.has(to.id))
				.map(to => ({
					id: to.id,
					title: to.title?.[this.locale] || to.title || 'Unknown'
				}))
		},
		filteredSpecificProvisions() {
			if (!this.model.specificProvisions) return []
			
			if (!this.selectedTourOperatorFilter || this.selectedTourOperatorFilter.length === 0) {
				return this.model.specificProvisions
			}
			
			const selectedIds = this.selectedTourOperatorFilter.map(filter => filter.id)
			
			return this.model.specificProvisions.filter(provision => {
				return provision.resellers && provision.resellers.some(resellerId => selectedIds.includes(resellerId))
			})
		}
	},
	methods: {
		toggle(index) {
			this.expandedSpecificProvision = this.model.specificProvisions[index]
			if (this.expanded === index) {
				this.expanded = null
			}
			else {
				this.expanded = index
			}
		},
		getOriginalIndex(provision) {
			return this.model.specificProvisions.findIndex(p => p.id === provision.id)
		},
		onTourOperatorFilterChange(value) {
			this.selectedTourOperatorFilter = value || []
			// Reset expanded row when filter changes
			this.expanded = null
		},
		getResellerNames(sProvision) {
			const sp = this.tourOperators.find(x => x.id === sProvision.resellers[0])
			let spTitle = `${sp?.title?.[this.locale]}`
			if (sProvision.resellers.length > 1) {
				spTitle += `, + ${sProvision.resellers.length - 1} ${this.$t('text.more')}`
			}
			return spTitle
		},
		openDialog(val) {
			this.specificProvision = val
			this.$nextTick(() => {
				this.$refs.specificProvisionDialog.open()
			})
		},
		openDeleteDialog(val) {
			this.deleteIndex = val
			this.$nextTick(() => {
				this.$refs.deleteDialog.show = true
			})
		},
		async onDeleteConfirmed() {
			await this.loadingOverlay(async () => {
				try {
				const tourOperatorExecutive = new TourOperatorExecutive(this)
				const provision = this.model.specificProvisions[this.deleteIndex]
				await tourOperatorExecutive.deleteSpecificProvision(this.serviceProviderId, provision.id)
				this.model.specificProvisions.splice(this.deleteIndex, 1)

				this.showSuccessToast()
				}
				catch (error) {
					this.showError(error.message)
				}
				finally {
					this.$nextTick(() => {
						this.$refs.deleteDialog.show = false
					})
				}
			}, 'deleteSpecificProvision')
			
			this.provisionLoading = false
		},
		async getTourOperators() {
			this.spLoading = true
			try {
				const tourOperatorExecutive = new TourOperatorExecutive(this)
				const operators = await tourOperatorExecutive.getTourOperators(this.serviceProviderId, this.clientId)
				this.tourOperators = operators?.map(x => ({ id: x.sys.id, title: x.fields.title }))
			}
			catch (error) {
				this.errorTitle = this.$t('text.ERROR')
				this.errorDetail = error.response?.error ?? error
			}
			this.spLoading = false
		},
		async getProductGroups() {
			this.productGroupLoading = true
			try {
				const installationId = this.$store.state.selectedClient.fields.installationId.de
				const url = `/peak/products/${installationId}?serviceProviderId=${this.serviceProviderExternalId}`
				const res = await this.$httpGet(url)

				// Get unique attribute set IDs from SP's products
				const attributeSetIdsWithProducts = new Set(res.products?.map(p => p.attribute_set_id) ?? [])

				// Filter attribute sets to only include ones that have at least one product
				this.productGroups = res.attributeSets
					?.filter(x => attributeSetIdsWithProducts.has(x.attribute_set_id))
					.map(x => ({ id: x.attribute_set_id, title: x.attribute_set_name }))
			}
			catch (error) {
				this.errorTitle = this.$t('text.ERROR')
				this.errorDetail = error.response?.error ?? error
			}
			this.productGroupLoading = false
		},
		async updateSpecificProvision(provision) {
			this.provisionLoading = true
			try {
				const tourOperatorExecutive = new TourOperatorExecutive(this)
				provision = await tourOperatorExecutive.upsertSpecificProvision(this.serviceProviderId, this.model.id, provision)
				const index = this.model.specificProvisions.findIndex(x => x.id === provision.id)
				if (index !== -1) {
					
					this.model.specificProvisions[index] = provision
				}
				else {
					this.model.specificProvisions.push(provision)
				}
			}
			catch (error) {
				this.showError(error.message)
			}
			this.specificProvision = {}
			this.provisionLoading = false
		},
		async updateGeneralProvision() {
			this.provisionLoading = true
			try {
				const tourOperatorExecutive = new TourOperatorExecutive(this)
				if (this.model.provision === "") {
					this.model.provision = null
				}
				this.model = await tourOperatorExecutive.upsertGeneralProvision(this.serviceProviderId, this.model)
			}
			catch (error) {
				this.showError(error.message)
			}
			this.provisionLoading = false
		},
		async getGeneralProvision() {
			this.provisionLoading = true
			try {
				const tourOperatorExecutive = new TourOperatorExecutive(this)
				this.model = await tourOperatorExecutive.getOrUpsertGeneralProvision(this.serviceProviderId, this.model)
			}
			catch (error) {
				this.showError(error.message)
			}
			this.provisionLoading = false
		},
		// async hasUnlockedTradehubApps() {
		// 	this.provisionLoading = true
		// 	const tourOperatorExecutive = new TourOperatorExecutive(this)
		// 	const hasUnlockedTradehubApps = await tourOperatorExecutive.hasUnlockedTradehubApps(this.userEmail, this.clientId)
		// 	if (!hasUnlockedTradehubApps) {
		// 		this.$router.push('/dashboard')
		// 	} else {
		// 		this.getGeneralProvision()
		// 		this.getTourOperators()
		// 		this.getProductGroups()
		// 	}
		// }
	},
	async mounted() {
		this.model.clientId = this.clientId
		this.model.serviceProviderId = this.serviceProviderId
		// await this.hasUnlockedTradehubApps()
		this.getGeneralProvision()
		this.getTourOperators()
		this.getProductGroups()
	},
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_common-form.scss';
.TradeHubSettingsView {
	.common-sidebar {
		:deep(.v-expansion-panel-title) {
			padding-top: 24px;
		}
	}
	.content-container {
		padding: 16px;
		margin-top: 60px;

		.FieldSet {
			&:first-of-type {
				.Field {
					padding-top: 24px;
					margin-bottom: 0px;
				}
			}
			&:last-of-type {
				padding-top: 24px;
			}
		}
		.specific-provisions {
			&__header {
				display: flex;
				align-items: center;
				justify-content: space-between;
				gap: 16px;
				padding: 16px 0;
				// border-bottom: 1px solid #e5e5e5;
				margin-bottom: 16px;
			}
			&__filter {
				flex: 1;
				min-width: 0;
			}
			&__empty {
				text-align: center;
				padding: 24px 0;
				
				> p {
					color: #8F8F8F;
					font-size: 15px;
					font-style: normal;
					font-weight: 400;
					line-height: 20px;
					letter-spacing: -0.037px;
					margin: 0;
				}
			}
			.add-button {
				flex-shrink: 0;
			}
			&__table {
				width: 100%;
				border-collapse: collapse;
				> thead {
					border-bottom: 1px solid #e5e5e5;
					> th {
						padding: 14px 12px;
						font-size: 15px;
						font-weight: 500;
						line-height: 20px;
						letter-spacing: -0.037px;
						text-align: left;
						&:nth-child(1) {
							padding-left: 26px
						}
					}
				}
				> tbody {
					> tr {
						border-bottom: 1px solid #e5e5e5;
					}
					> tr:not(.expanded-row) {
						height: 60px;
						&.open > td:first-child > div > i::before {
							transform: rotate(90deg);
						}
						> td {
							padding: 0px 12px;
							&:first-child {
								padding-left: 0px;
								> div {
									display: flex;
									> p {
										padding-left: 3px;
									}
									> i::before {
										margin-top: -3px;
										transition: transform 0.2s ease-in-out;
									}
								}
							}
							&:last-child {
								width: 100px;
								> div {
									display: flex;
									justify-content: space-between;
								}
							}
							p {
								color: #000;
								font-size: 15px;
								font-weight: 400;
								line-height: 20px;
								letter-spacing: -0.037px;
							}
						}
						&:hover {
							background-color: #f9f9f9;
							cursor: pointer;
						}
					}
				}
			}
		}
	}
}

// Allow clicking on table rows to expand/collapse even when disclosure is locked (operator impersonation)
:deep(.v-expansion-panel-text.disabled) {
	.specific-provisions__table > tbody > tr:not(.expanded-row) {
		pointer-events: auto;
		cursor: pointer;
		
		// But keep the edit/delete buttons disabled
		td:last-child {
			pointer-events: none;
			
			i {
				cursor: not-allowed;
				opacity: 0.5;
			}
		}
	}
	.add-button > button {
		pointer-events: none !important;
	}
}
</style>